import { AuditRow, ShownReason } from "../../models/AuditRow";
import { AuditTrip, DetectedEvent, displayReason, RefuelEntry } from "../../models/AuditTrip";
import { FuelType } from "@/constants/FuelTypeGroups";

/** Field names of the grid columns; the export uses the same names. */
export const AUDIT_FIELDS = {
  duration: "duration",
  fuelType: "fuelType",
  adBlue: "adBlue",
  licensePlate: "licensePlate",
  vin: "vin",
  date: "date",
  driver: "driver",
  distance: "distance",
  firstStart: "firstStart",
  startAddress: "startAddress",
  lastStop: "lastStop",
  stopAddress: "stopAddress",
  consumption: "consumption",
  reason: "reason",
  dutyRoster: "dutyRoster",
  odometerStart: "odometerStart",
  odometerEnd: "odometerEnd",
  refuel: "refuel",
  map: "map",
} as const;

export function rowDate(row: AuditRow): Date {
  return row.date;
}

export function rowDistance(row: AuditRow): number | undefined {
  return row.rowType === "day" ? row.distance : row.trip.distance;
}

export function rowFirstStart(row: AuditRow): Date {
  return row.rowType === "day" ? row.firstStart : row.trip.start;
}

export function rowLastStop(row: AuditRow): Date | undefined {
  return row.rowType === "day" ? row.lastStop : row.trip.stop;
}

/** The entry whose start address represents the row. */
export function rowStartTrip(row: AuditRow): AuditTrip {
  return row.rowType === "day" ? row.firstTrip : row.trip;
}

/** The entry whose stop address represents the row. */
export function rowStopTrip(row: AuditRow): AuditTrip {
  return row.rowType === "day" ? row.lastTrip : row.trip;
}

export function rowConsumption(row: AuditRow): { value?: number; fuelType?: FuelType } {
  const fuelType = row.device.fuelType;
  return { value: row.rowType === "day" ? row.consumption : row.trip.consumption, fuelType };
}

export function rowReasons(row: AuditRow): { reasons: ShownReason[]; missing: boolean } {
  if (row.rowType === "day") return { reasons: row.reasons, missing: row.hasMissingReason };
  const shownReason = displayReason(row.trip);
  return {
    reasons: shownReason ? [{ label: shownReason, taxClass: row.trip.taxClass }] : [],
    missing: !shownReason,
  };
}

export function rowReasonText(row: AuditRow): string {
  return rowReasons(row)
    .reasons.map(reason => reason.label)
    .join(", ");
}

export function rowDutyRosters(row: AuditRow): string[] {
  if (row.rowType === "day") return row.dutyRosterNumbers;
  const number = row.trip.dutyRoster?.dutyRosterNumber;
  return number ? [number] : [];
}

/** Drivers of the row; empty for an unbooked trip. */
export function rowDriverIds(row: AuditRow): string[] {
  if (row.rowType === "day") return row.driverIds;
  return row.trip.driverId ? [row.trip.driverId] : [];
}

export function rowRefuelLiters(row: AuditRow): number | undefined {
  return row.rowType === "day" ? row.refuelLiters : row.trip.refuelLiters;
}

/** Driving time; undefined for an entry without an end. */
export function rowDurationMs(row: AuditRow): number | undefined {
  return row.rowType === "day" ? row.durationMs : row.trip.durationMs;
}

export function rowAdBlueLiters(row: AuditRow): number | undefined {
  return row.rowType === "day" ? row.adBlueLiters : row.trip.adBlueLiters;
}

export function rowOdometerStartKm(row: AuditRow): number | undefined {
  return row.rowType === "day" ? row.odometerStartKm : row.trip.odometerStartKm;
}

export function rowOdometerEndKm(row: AuditRow): number | undefined {
  return row.rowType === "day" ? row.odometerEndKm : row.trip.odometerEndKm;
}

/** One line of the refuel tooltip: a transaction, or a detected event standing in for one. */
export type ShownRefuel = { kind: "transaction"; entry: RefuelEntry } | { kind: "detected"; event: DetectedEvent };

/**
 * What stands behind the row's refuelled amount, entry by entry: the fuel transactions of each
 * trip, or for a trip without one Geotab's detected fill-ups and charging sessions.
 */
export function rowShownRefuels(row: AuditRow): ShownRefuel[] {
  return rowTrips(row).flatMap((trip): ShownRefuel[] =>
    trip.refuelFromDetected
      ? trip.detectedEvents.map(event => ({ kind: "detected", event }))
      : trip.refuels.filter(entry => entry.kind === "fuel").map(entry => ({ kind: "transaction", entry })),
  );
}

export function rowAdBlueRefuels(row: AuditRow): RefuelEntry[] {
  return rowTrips(row)
    .flatMap(trip => trip.refuels)
    .filter(entry => entry.kind === "adBlue");
}

export function rowTrips(row: AuditRow): AuditTrip[] {
  return row.rowType === "day" ? row.trips : [row.trip];
}
